import { OmsOrderDetail, OmsOrderOperateHistory } from 'types/oms';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import DualTimezoneTime from 'components/dual-timezone-time';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { findLabelByValue } from 'commons/options';
import { Table, Card, Image } from 'antd';
import { History } from 'lucide-react';
import commonApi from 'apis/common';

type OrderOperateNotesProps = {
  omsOrderDetail: OmsOrderDetail;
  stockPlace?: string;
};

const OperationHistoryImages = ({ imageKeys }: { imageKeys: string[] }) => {
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    let isActive = true;

    Promise.all(
      imageKeys.filter(Boolean).map(async (key) => {
        try {
          const result = await commonApi.getSignedFileUrl(key);
          return result.url;
        } catch {
          return null;
        }
      }),
    ).then((resolvedUrls) => {
      if (isActive) {
        setUrls(resolvedUrls.filter((url): url is string => Boolean(url)));
      }
    });

    return () => {
      isActive = false;
    };
  }, [imageKeys]);

  if (urls.length === 0) return null;

  return (
    <Image.PreviewGroup>
      <div className="flex flex-wrap gap-2">
        {urls.map((url) => (
          <Image
            key={url}
            src={url}
            alt=""
            width={64}
            height={64}
            style={{ objectFit: 'cover' }}
          />
        ))}
      </div>
    </Image.PreviewGroup>
  );
};

const OrderOperateHistories = ({
  omsOrderDetail: { historyList },
  stockPlace,
}: OrderOperateNotesProps) => {
  const { orderStatusOptions } = useAppSelector(selectGlobalInfo);
  const columns: ColumnsType<OmsOrderOperateHistory> = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.operator} />,
        dataIndex: 'operateMan',
        key: 'operateMan',
      },
      {
        title: <Trans i18nKey={LOCALS.created_time} />,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: OmsOrderOperateHistory['createTime']) => {
          return <DualTimezoneTime time={createTime} stockPlace={stockPlace} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.order_status} />,
        dataIndex: 'orderStatus',
        key: 'orderStatus',
        render: (orderStatus: OmsOrderOperateHistory['orderStatus']) => {
          return findLabelByValue(orderStatus, orderStatusOptions);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.note} />,
        dataIndex: 'note',
        key: 'note',
        render(note: OmsOrderOperateHistory['note']) {
          return <div className="whitespace-pre-line">{note}</div>;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.related_images} />,
        dataIndex: 'operationImages',
        key: 'operationImages',
        render(operationImages: OmsOrderOperateHistory['operationImages']) {
          return operationImages?.length ? (
            <OperationHistoryImages imageKeys={operationImages} />
          ) : null;
        },
      },
    ];
  }, [orderStatusOptions, stockPlace]);

  if (historyList?.length === 0) return null;

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <History className="w-5 h-5" />
        <Trans i18nKey={LOCALS.operate_histories} />
      </div>

      <Table
        bordered
        size="small"
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        rowKey={'id'}
        pagination={false}
        columns={columns}
        dataSource={historyList || []}
      />
    </Card>
  );
};

export default OrderOperateHistories;
